/**
 * El logotipo del emisor — `RC-0009` (feature `invoice_logo`), `URS §106`.
 *
 * Vive aparte de `PantallaEmisor` porque no es un dato fiscal: no entra en el
 * formulario de datos del emisor, no se confirma con ellos y cambiarlo no toca
 * la confirmación fiscal (`OD-0015 D-03`). Es una tarjeta más de la misma
 * pantalla.
 *
 * ## Lo que esta pantalla NO decide
 *
 * Si el plan incluye el logotipo lo dice la API en `incluidoEnElPlan`, ya
 * resuelto. `URS §105` prohíbe que el navegador compare nombres de plan: un
 * `if (plan === "BUSINESS")` aquí concedería o denegaría en silencio el día que
 * entre un plan nuevo, y en el eje de plan equivocarse es facturable.
 *
 * El tamaño sí se mira aquí, y además en la API. No es duplicar una regla: es
 * no hacerle subir 4 MB a alguien para decirle al final que no caben. La que
 * decide sigue siendo la API.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { api, LOGO_MAX_BYTES, type ErrorApi, type EstadoLogo } from "../api/cliente";
import { t } from "../i18n/es";
import { Aviso, Boton, Tarjeta } from "../ui/primitivas";

type Mensaje = { readonly tono: "exito" | "aviso" | "error"; readonly texto: string };

/**
 * El mensaje de un error de la API, con el `reason` que ella manda.
 *
 * Se traducen los dos que el usuario puede arreglar por su cuenta; para el
 * resto vale el mensaje de la API, que ya viene escrito para leerse. Inventar
 * aquí un texto para cada caso sería tener dos sitios donde se explica lo mismo
 * y uno donde se queda viejo.
 */
function mensajeDe(error: ErrorApi | null): string {
  if (error === null) return t.logo.error;
  if (error.motivo === "LOGO_TOO_LARGE") return t.logo.demasiadoGrande;
  if (error.motivo === "NO_ES_IMAGEN") return t.logo.noEsImagen;
  return error.message;
}

export function TarjetaLogo({ hayConexion }: { readonly hayConexion: boolean }) {
  const [estado, setEstado] = useState<EstadoLogo | null>(null);
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [mensaje, setMensaje] = useState<Mensaje | null>(null);
  const entrada = useRef<HTMLInputElement>(null);

  const cargar = useCallback(async () => {
    const r = await api.estadoLogo();
    setCargando(false);
    // Un fallo al leer el estado no es un error que merezca ruido: la tarjeta
    // simplemente no se puede pintar, y el resto de la pantalla sigue viva.
    if (r.ok) setEstado(r.datos);
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const subir = async (fichero: File): Promise<void> => {
    setMensaje(null);
    if (fichero.size > LOGO_MAX_BYTES) {
      setMensaje({ tono: "error", texto: t.logo.demasiadoGrande });
      return;
    }
    setOcupado(true);
    const r = await api.subirLogo(fichero);
    setOcupado(false);
    // El input se limpia SIEMPRE, también tras un fallo: si no, volver a elegir
    // el mismo fichero no dispara `change` y parece que el botón no funciona.
    if (entrada.current !== null) entrada.current.value = "";
    if (!r.ok) {
      setMensaje({ tono: "error", texto: mensajeDe(r.error) });
      return;
    }
    setMensaje({ tono: "exito", texto: t.logo.subido });
    await cargar();
  };

  const quitar = async (): Promise<void> => {
    setMensaje(null);
    setOcupado(true);
    const r = await api.quitarLogo();
    setOcupado(false);
    if (!r.ok) {
      setMensaje({ tono: "error", texto: mensajeDe(r.error) });
      return;
    }
    setMensaje({ tono: "aviso", texto: t.logo.quitado });
    await cargar();
  };

  if (cargando) {
    return (
      <Tarjeta titulo={t.logo.titulo}>
        <p className="ayuda-neutra">{t.logo.cargando}</p>
      </Tarjeta>
    );
  }
  if (estado === null) return null;

  if (!estado.incluidoEnElPlan) {
    return (
      <Tarjeta titulo={t.logo.titulo}>
        <Aviso tono="aviso">{t.logo.sinPlan}</Aviso>
        <a className="boton boton-secundario enlace-boton" href="/plan">
          {t.logo.verPlanes}
        </a>
      </Tarjeta>
    );
  }

  /*
    Sin almacen en el despliegue no se ofrece subir nada. Es la misma regla que
    el resto del repositorio -sin certificado no se llama a la AEAT, sin claves
    no se cobra-: una funcionalidad que depende de un recurso del despliegue no
    se activa a medias. Y al usuario se le dice, en vez de darle un boton que
    falla.
  */
  if (!estado.disponible) {
    return (
      <Tarjeta titulo={t.logo.titulo}>
        <Aviso tono="aviso">{t.logo.noDisponible}</Aviso>
      </Tarjeta>
    );
  }

  const logo = estado.logo;
  return (
    <Tarjeta titulo={t.logo.titulo}>
      <p className="ayuda-neutra">{t.logo.detalle}</p>
      <p className="ayuda-neutra">{t.logo.requisitos}</p>

      {logo === null ? (
        <p className="ayuda-neutra">{t.logo.ninguno}</p>
      ) : (
        <div className="logo-actual">
          {/*
            NO se muestra la imagen, y no es que falte: el propietario decidió el
            2026-10-06 que el logotipo no se devuelva nunca por HTTP. Sale sólo
            dentro del PDF que genera el servidor, así que aquí se dice QUÉ hay
            —el fichero y lo que ocupa— y dónde verlo.

            Un fichero que nunca se devuelve no puede ejecutarse en el navegador
            de nadie. Es más seguro que cualquier cabecera, y cuesta esto.
          */}
          <p className="logo-puesto">{logo.originalName}</p>
          <p className="ayuda-neutra">{t.logo.ocupa(logo.tamanoLegible)}</p>
          {/*
            OD-0044 D-03 y D-04: un archivo que no cuadra deja de salir en las
            facturas, y aqui se dice —con lo que lo arregla—. `null` es
            «todavia sin comprobar» y no avisa de nada: avisar de lo que nadie
            ha mirado seria asustar sin motivo.
          */}
          {logo.integridad === "OK" || logo.integridad === null ? (
            <p className="ayuda-neutra">{t.logo.dondeVerlo}</p>
          ) : (
            <Aviso tono="error">{t.logo.noCuadra}</Aviso>
          )}
        </div>
      )}

      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
      {!hayConexion ? <Aviso tono="aviso">{t.comun.sinRed}</Aviso> : null}

      <div className="fila-botones">
        {/*
          Un `input[type=file]` oculto con el botón de verdad delante: el control
          nativo no se puede estilar y su texto lo pone el navegador, en el
          idioma del navegador. El input sigue ahí y sigue siendo accesible.
        */}
        <input
          ref={entrada}
          id="logo-fichero"
          className="entrada-fichero"
          type="file"
          accept="image/png,image/jpeg"
          disabled={ocupado || !hayConexion}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f !== undefined) void subir(f);
          }}
        />
        <label className="boton boton-secundario enlace-boton" htmlFor="logo-fichero">
          {ocupado ? t.logo.subiendo : t.logo.elegir}
        </label>
        {logo !== null ? (
          <Boton variante="peligro" onClick={() => void quitar()} deshabilitado={ocupado || !hayConexion}>
            {ocupado ? t.logo.quitando : t.logo.quitar}
          </Boton>
        ) : null}
      </div>
    </Tarjeta>
  );
}
